import { Watering } from 'domain/models/api.models';
import { WidgetProps } from './Widgets';
import { Button, Input } from 'components';
import { useMemo } from 'react';
import { useFormData } from 'domain/hooks';
import { DatePickerComponent } from './DatePicker';
import { atStartOfDay } from 'domain/utils/date.utils';
import { useTranslation } from 'react-i18next';

const colorStops = [0, 2, 8, 16, 32, 64, 128, 256, 512, 1024, 2048, 10000];

const asColor = (lm2: number) => {
	const index = colorStops.findIndex((v) => v >= lm2);
	const s = 100 - (index / colorStops.length) * 100;
	return `hsl(200, 100%, ${s}%)`;
};

export const WateringComponent = ({
	value,
	onChange,
}: WidgetProps<Watering[]>) => {
	const { t } = useTranslation(['labels']);

	const [form, setValue, setFormData] = useFormData<Watering>({
		date: atStartOfDay(new Date()),
		lm2: 0,
	});

	const asArray = useMemo(() => {
		if (!value) {
			return [];
		}
		if (value.constructor === Array) {
			return value;
		}
		return [];
	}, [value]);

	const selectDate = (date: string) => {
		// Find the value
		const value = asArray.find((v) => v.date === date);

		// If no value, set the form to the date
		setFormData({
			date: date,
			lm2: value?.lm2 ?? 0,
		});
	};

	const last14days = useMemo(() => {
		const values = [];

		for (let day = -14; day <= 0; day++) {
			const date = Date.now() + day * 24 * 60 * 60 * 1000;
			const dayDate = atStartOfDay(new Date(date));
			const watering = asArray.find((v) => v.date === dayDate);
			values.push({
				day: new Date(date).getDate(),
				lm2: watering?.lm2 ?? 0,
				date: dayDate,
			});
		}
		return values;
	}, [asArray]);

	return (
		<div className='flex w-full flex-col items-center justify-between'>
			<div className='min-h-12 my-6 flex w-full w-full flex-wrap items-center justify-center border-sky-50'>
				{last14days.map((v) => {
					const color = asColor(v.lm2);
					return (
						<div
							className='cursor-pointer text-center'
							key={`watering-cell-${v.day}`}
							onClick={() => selectDate(v.date)}>
							<div
								className={`m-[1px] h-5 w-5	border`}
								style={{
									backgroundColor: color,
								}}></div>
							<div className='text-xs'>{v.day}</div>
						</div>
					);
				})}
			</div>

			<div className='flex w-full items-start justify-between'>
				<div className='flex w-full items-center justify-start'>
					<label className='flex flex-col'>
						<DatePickerComponent
							className='mr-2 w-32'
							value={form.date}
							onChange={(v) => setValue('date')(atStartOfDay(v))}
						/>
						<span className='text-sm'>{t('labels:date')}</span>
					</label>
					<label className='flex flex-col'>
						<Input
							type='number'
							className='mr-2 w-32'
							value={form.lm2}
							onChange={(e) => setValue('lm2')(parseFloat(e.target.value))}
						/>
						<span className='text-sm'>l/m2</span>
					</label>
				</div>

				<label className='flex flex-col'>
					<Button
						onClick={() => {
							const actual = [...asArray];
							// Update by date
							const index = actual.findIndex((v) => v.date === form.date);
							if (index !== -1) {
								actual[index] = form;
							} else {
								actual.push(form);
							}
							onChange(actual);
						}}>
						+
					</Button>
				</label>
			</div>
		</div>
	);
};
